<script setup>
import { computed, ref, watch } from 'vue';
import { useNexceoVoice } from 'dashboard/composables/useNexceoVoice';
import Button from 'dashboard/components-next/button/Button.vue';

// M29.2 Voice Experience — Conversation Header Call Button / Voice Toolbar.
// Provider selection is automatic: Nexceo::Voice::CapabilitiesController
// returns at most one provider for a given conversation's inbox (a channel
// is either Twilio-voice-enabled or WhatsApp-calling-enabled, never both),
// so there is exactly one button, never a picker — "no duplicated UI".
const props = defineProps({
  conversationId: { type: [String, Number], required: true },
  phoneNumber: { type: String, default: '' },
});

const { fetchCapabilities, startCall, callForConversation, hasActiveCall } =
  useNexceoVoice();

const provider = ref(null);
const isLoadingCapabilities = ref(false);
const isStarting = ref(false);

const loadCapabilities = async () => {
  if (!props.conversationId) return;
  isLoadingCapabilities.value = true;
  const result = await fetchCapabilities(props.conversationId);
  provider.value = result?.provider || null;
  isLoadingCapabilities.value = false;
};

watch(() => props.conversationId, loadCapabilities, { immediate: true });

const existingCall = computed(() => callForConversation(props.conversationId));

const shouldRender = computed(
  () => !!provider.value && !!props.phoneNumber && !existingCall.value
);

const isDisabled = computed(
  () => isLoadingCapabilities.value || isStarting.value || hasActiveCall.value
);

const onClick = async () => {
  if (isDisabled.value) return;
  isStarting.value = true;
  await startCall({
    conversationId: props.conversationId,
    provider: provider.value,
    to: props.phoneNumber,
  });
  isStarting.value = false;
};
</script>

<template>
  <Button
    v-if="shouldRender"
    icon="i-ri-phone-fill"
    variant="ghost"
    size="sm"
    :disabled="isDisabled"
    :is-loading="isStarting"
    data-testid="conversation-header-call-button"
    @click="onClick"
  />
</template>
